import React, { useState, useEffect } from 'react';
import { 
  IonPage, 
  IonHeader, 
  IonToolbar, 
  IonTitle, 
  IonContent, 
  IonLoading, 
  IonToast,
  IonSegment,
  IonSegmentButton,
  IonLabel,
  IonButtons,
  IonBackButton
} from '@ionic/react';
import { 
  Box, 
  Card, 
  CardContent, 
  Typography, 
  TextField, 
  Button, 
  MenuItem, 
  Select, 
  FormControl, 
  InputLabel,
  Divider,
  CircularProgress,
  IconButton,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  Autocomplete,
  Stepper,
  Step,
  StepLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { Add, Delete, ExpandMore, ShoppingCart, History, Check, Close, Info } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { getDepartmentInventory,getMyDepartmentRequests,addDepartmentRequest } from '../../api/accounting/accounting';
// import { addDepartmentRequest, getMyDepartmentRequests } from '../../api/stock-transfer/department-request';

const StyledCard = styled(Card)(({ theme }) => ({
  margin: theme.spacing(2),
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadius * 2,
  boxShadow: theme.shadows[3],
  [theme.breakpoints.down('sm')]: {
    margin: theme.spacing(1),
    padding: theme.spacing(2),
  },
}));

const PageContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1),
  },
}));

interface RequestItem {
  id: string;
  item_id: string;
  stock_name: string;
  quantity: number;
  unit_price: string;
  available_quantity: string;
  department_id?: string;
}

interface StockItem {
  item_id: string;
  stock_name: string;
  quantity: string;
  unit_price: string;
  department_id: string;
  department_name: string;
  department_type: string;
  last_updated: string;
}

const ItemRequestPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'new' | 'history'>('new');
  const [stockItems, setStockItems] = useState<StockItem[]>([]);
  const [requestItems, setRequestItems] = useState<RequestItem[]>([]);
  const [selectedItem, setSelectedItem] = useState<StockItem | null>(null);
  const [quantity, setQuantity] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [requestsHistory, setRequestsHistory] = useState<any[]>([]);
  const [showSubmitDialog, setShowSubmitDialog] = useState(false);
  const [requestNotes, setRequestNotes] = useState('');

  // Departments list (could be fetched from API)
  const departments = [
    { id: 'ER001', name: 'Emergency Room' },
    { id: 'OPD002', name: 'Outpatient Department' },
    { id: 'WARD003', name: 'General Ward' },
    { id: 'SURG004', name: 'Surgery' },
    { id: 'LAB005', name: 'Laboratory' },
    { id: 'PHARM006', name: 'Pharmacy' },
  ];

  const currentDepartment = departments[0]; // In real app, get from user context

  useEffect(() => {
    fetchStockItems();
    if (activeTab === 'history') {
      fetchRequestHistory();
    }
  }, [activeTab]);

  const fetchStockItems = async () => {
    setLoading(true);
    try {
      const payload = {
        department_id: 'WH001', // Warehouse ID
        page: 1,
        per_page: 50,
        search: searchQuery
      };
      const response = await getDepartmentInventory(payload);
      console.log({response});
      if (response.data && Array.isArray(response.data.data)) {
        setStockItems(response.data.data);
      }
    } catch (error) {
      showErrorToast('Failed to fetch stock items');
    } finally {
      setLoading(false);
    }
  };

  const fetchRequestHistory = async () => {
    try {
      const response = await getMyDepartmentRequests();
      console.log("Request history response:", response.data);
      if (response.data && Array.isArray(response.data.data)) {
        
        setRequestsHistory(response.data.data);
      }
    } catch (error) {
      showErrorToast('Failed to fetch request history');
    }
  };

  const showSuccessToast = (message: string) => {
    setToastMessage(message);
    setToastType('success');
    setShowToast(true);
  };

  const showErrorToast = (message: string) => {
    setToastMessage(message);
    setToastType('error');
    setShowToast(true);
  };

  const addItemToRequest = () => {
    if (!selectedItem || !quantity || parseInt(quantity) <= 0) {
      showErrorToast('Please select an item and enter valid quantity');
      return;
    }

    if (parseInt(quantity) > parseInt(selectedItem.quantity)) {
      showErrorToast(`Only ${selectedItem.quantity} items available in stock`);
      return;
    }
    const existingItem = requestItems.find(item => item.item_id === selectedItem.item_id);
    if (existingItem) {
      showErrorToast('Item already added to request');
      return;
    }

    const newItem: RequestItem = {
      id: Date.now().toString(),
      item_id: selectedItem.item_id,
      stock_name: selectedItem.stock_name,
      quantity: parseInt(quantity),
      unit_price: selectedItem.unit_price,
      available_quantity: selectedItem.quantity,
      department_id: currentDepartment.id
    };

    setRequestItems([...requestItems, newItem]);
    setSelectedItem(null);
    setQuantity('');
    showSuccessToast('Item added to request');
  };

  const removeItemFromRequest = (id: string) => {
    setRequestItems(requestItems.filter(item => item.id !== id));
  };

  const calculateTotalValue = () => {
    return requestItems.reduce((total, item) => {
      return total + (item.quantity * parseFloat(item.unit_price));
    }, 0);
  };

  const handleSubmitRequest = async () => {
    if (requestItems.length === 0) {
      showErrorToast('Please add at least one item to request');
      return;
    }

    setLoading(true);
    try {
      const requestData = {
        department_id: "WH001", // Warehouse ID
        department_name: currentDepartment.name,
        items: requestItems.map(item => ({
          item_id: item.item_id,
          stock_name: item.stock_name,
          quantity: item.quantity,
          unit_price: item.unit_price,
          notes: requestNotes
        })),
        total_value: calculateTotalValue().toFixed(2)
      };

     const response =  await addDepartmentRequest(requestData);
    //  console.log({response});
      
      showSuccessToast('Request submitted successfully!');
      setRequestItems([]);
      setRequestNotes('');
      setShowSubmitDialog(false);
      setActiveTab('history');
    } catch (error) {
      showErrorToast('Error submitting request');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toLowerCase()) {
      case 'approved': return 'success';
      case 'pending': return 'warning';
      case 'rejected': return 'error';
      case 'partially_fulfilled': return 'info';
      case 'delivered': return 'primary';
      default: return 'default';
    }
  };

  return (
    <IonPage className='pageDown'>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/dashboard" />
          </IonButtons>
          <IonTitle>Department Stock Request</IonTitle>
        </IonToolbar>
        <IonToolbar>
          <IonSegment 
            value={activeTab} 
            onIonChange={(e) => setActiveTab(e.detail.value as any)}
            style={{ width: '100%' }}
          >
            <IonSegmentButton value="new">
              <IonLabel>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <ShoppingCart fontSize="small" />
                  New Request
                  {requestItems.length > 0 && (
                    <Chip 
                      label={requestItems.length} 
                      size="small" 
                      color="primary"
                    />
                  )}
                </Box>
              </IonLabel>
            </IonSegmentButton>
            <IonSegmentButton value="history">
              <IonLabel>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <History fontSize="small" />
                  Request History
                </Box>
              </IonLabel>
            </IonSegmentButton>
          </IonSegment>
        </IonToolbar>
      </IonHeader>
      
      <IonContent>
        <PageContainer>
          {activeTab === 'new' ? (
            <>
              <StyledCard>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h5" component="h2">
                      New Stock Request
                    </Typography>
                    <Chip 
                      label={`Department: ${currentDepartment.name}`}
                      color="primary"
                      variant="outlined"
                    />
                  </Box>

                  <Alert severity="info" sx={{ mb: 3 }}>
                    Request items from Central Warehouse for your department use.
                  </Alert>

                  <Grid container spacing={3}>
                    <Grid item xs={12} md={8}>
                      <FormControl fullWidth>
                        <Autocomplete
                          options={stockItems}
                          getOptionLabel={(option) => 
                            `${option.stock_name} - Available: ${option.quantity} - $${option.unit_price}`
                          }
                          value={selectedItem}
                          onChange={(_, newValue) => setSelectedItem(newValue)}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Search and Select Item"
                              variant="outlined"
                              fullWidth
                              onChange={(e) => setSearchQuery(e.target.value)}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  fetchStockItems();
                                }
                              }}
                              InputProps={{
                                ...params.InputProps,
                                endAdornment: (
                                  <>
                                    {params.InputProps.endAdornment}
                                  </>
                                ),
                              }}
                            />
                          )}
                          renderOption={(props, option) => (
                            <li {...props}>
                              <Box sx={{ width: '100%' }}>
                                <Typography variant="body1">
                                  {option.stock_name}
                                </Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                                  <Typography variant="caption" color="text.secondary">
                                    ID: {option.item_id}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Available: {option.quantity}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Price: ${option.unit_price}
                                  </Typography>
                                </Box>
                              </Box>
                            </li>
                          )}
                        />
                      </FormControl>
                    </Grid>

                    <Grid item xs={12} md={2}>
                      <TextField
                        fullWidth
                        label="Quantity"
                        type="number"
                        value={quantity}
                        onChange={(e) => setQuantity(e.target.value)}
                        inputProps={{ 
                          min: 1,
                          max: selectedItem ? parseInt(selectedItem.quantity) : undefined
                        }}
                        disabled={!selectedItem}
                      />
                    </Grid>

                    <Grid item xs={12} md={2}>
                      <Button
                        fullWidth
                        variant="contained"
                        startIcon={<Add />}
                        onClick={addItemToRequest}
                        disabled={!selectedItem || !quantity}
                        sx={{ height: '56px' }}
                      >
                        Add Item
                      </Button>
                    </Grid>
                  </Grid>

                  {requestItems.length > 0 && (
                    <Box sx={{ mt: 4 }}>
                      <Typography variant="h6" gutterBottom>
                        Request Items ({requestItems.length})
                      </Typography>
                      <TableContainer component={Paper} sx={{ mt: 2 }}>
                        <Table>
                          <TableHead>
                            <TableRow sx={{ backgroundColor: 'action.hover' }}>
                              <TableCell><strong>Item Name</strong></TableCell>
                              <TableCell><strong>Item ID</strong></TableCell>
                              <TableCell align="right"><strong>Available</strong></TableCell>
                              <TableCell align="right"><strong>Request Qty</strong></TableCell>
                              <TableCell align="right"><strong>Unit Price</strong></TableCell>
                              <TableCell align="right"><strong>Total</strong></TableCell>
                              <TableCell align="center"><strong>Action</strong></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {requestItems.map((item) => (
                              <TableRow key={item.id} hover>
                                <TableCell>{item.stock_name}</TableCell>
                                <TableCell>
                                  <Chip label={item.item_id} size="small" variant="outlined" />
                                </TableCell>
                                <TableCell align="right">
                                  {item.available_quantity}
                                </TableCell>
                                <TableCell align="right">
                                  <Typography color={item.quantity > parseInt(item.available_quantity) ? 'error' : 'inherit'}>
                                    {item.quantity}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">
                                  ${parseFloat(item.unit_price).toFixed(2)}
                                </TableCell>
                                <TableCell align="right">
                                  <Typography variant="subtitle2">
                                    ${(item.quantity * parseFloat(item.unit_price)).toFixed(2)}
                                  </Typography>
                                </TableCell>
                                <TableCell align="center">
                                  <IconButton
                                    size="small"
                                    color="error"
                                    onClick={() => removeItemFromRequest(item.id)}
                                  >
                                    <Delete />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            ))}
                            <TableRow sx={{ borderTop: 2 }}>
                              <TableCell colSpan={5} align="right">
                                <Typography variant="subtitle1">
                                  <strong>Total Value:</strong>
                                </Typography>
                              </TableCell>
                              <TableCell align="right">
                                <Typography variant="h6" color="primary">
                                  <strong>${calculateTotalValue().toFixed(2)}</strong>
                                </Typography>
                              </TableCell>
                              <TableCell></TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>

                      <Box sx={{ mt: 3 }}>
                        <TextField
                          fullWidth
                          multiline
                          rows={3}
                          label="Additional Notes (Optional)"
                          value={requestNotes}
                          onChange={(e) => setRequestNotes(e.target.value)}
                          placeholder="Add any special instructions or notes for this request..."
                        />
                      </Box>

                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                        <Button
                          variant="outlined"
                          onClick={() => {
                            setRequestItems([]);
                            setRequestNotes('');
                          }}
                          disabled={loading}
                        >
                          Clear All
                        </Button>
                        <Button
                          variant="contained"
                          color="primary"
                          size="large"
                          endIcon={<Check />}
                          onClick={() => setShowSubmitDialog(true)}
                          disabled={requestItems.length === 0 || loading}
                        >
                          Submit Request
                        </Button>
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </StyledCard>
            </>
          ) : (
            <StyledCard>
              <CardContent>
                <Typography variant="h5" component="h2" gutterBottom>
                  Request History
                </Typography>

                {requestsHistory.length === 0 ? (
                  <Alert severity="info" sx={{ textAlign: 'center', py: 3 }}>
                    No previous requests found
                  </Alert>
                ) : (
                  requestsHistory.map((request) => (
                    <Accordion key={request.request_id} sx={{ mb: 2 }}>
                      <AccordionSummary expandIcon={<ExpandMore />}>
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          width: '100%',
                          alignItems: 'center',
                          flexWrap: 'wrap',
                          gap: 2
                        }}>
                          <Box>
                            <Typography variant="subtitle1">
                              Request #{request.request_id}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {new Date(request.created_at).toLocaleString()}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Chip 
                              label={request.status?.replace('_', ' ') || 'pending'}
                              color={getStatusColor(request.status) as any}
                              size="small"
                            />
                            <Typography variant="body2">
                              ${parseFloat(request.total_value || '0').toFixed(2)}
                            </Typography>
                          </Box>
                        </Box>
                      </AccordionSummary>
                      <AccordionDetails>
                        {request.notes && (
                          <Alert severity="info" sx={{ mb: 2 }}>
                            <Typography variant="subtitle2">Notes:</Typography>
                            {request.notes}
                          </Alert>
                        )}
                        
                        <TableContainer component={Paper}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Item</TableCell>
                                <TableCell align="right">Requested</TableCell>
                                <TableCell align="right">Approved</TableCell>
                                <TableCell align="right">Delivered</TableCell>
                               
                                <TableCell align="center">Status</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {request.items?.map((item: any,i) => (
                                <TableRow key={i} hover>
                                  <TableCell>
                                    <Box>
                                      <Typography variant="body2">{item.stock_name}</Typography>
                                      <Typography variant="caption" color="text.secondary">
                                        ID: {item.item_id}
                                      </Typography>
                                    </Box>
                                  </TableCell>
                                  <TableCell align="right">{item.requested_qty}</TableCell>
                                  <TableCell align="right">{item.approved_qty || '-'}</TableCell>
                                  <TableCell align="right">{item.approved_qty || '-'}</TableCell>
                              
                                  <TableCell align="center">
                                    <Chip
                                      label={item.status?.replace('_', ' ') || 'pending'}
                                      size="small"
                                      color={getStatusColor(item.status) as any}
                                    />
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </AccordionDetails>
                    </Accordion>
                  ))
                )}
              </CardContent>
            </StyledCard>
          )}
        </PageContainer>

        {/* Submit Confirmation Dialog */}
        <Dialog 
          open={showSubmitDialog} 
          onClose={() => setShowSubmitDialog(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Info color="primary" />
              Confirm Stock Request
            </Box>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" gutterBottom>
              You are about to submit a request for {requestItems.length} item(s) from Central Warehouse.
            </Typography>
            
            <Box sx={{ mt: 2, mb: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Summary:
              </Typography>
              {requestItems.slice(0, 3).map((item, index) => (
                <Typography key={index} variant="body2">
                  • {item.stock_name} - {item.quantity} units
                </Typography>
              ))}
              {requestItems.length > 3 && (
                <Typography variant="body2" color="text.secondary">
                  ...and {requestItems.length - 3} more items
                </Typography>
              )}
            </Box>
            
            <Typography variant="h6" color="primary" align="center">
              Total Value: ${calculateTotalValue().toFixed(2)}
            </Typography>
            
            {requestNotes && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="subtitle2">Your Notes:</Typography>
                {requestNotes}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowSubmitDialog(false)} disabled={loading}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmitRequest} 
              variant="contained" 
              color="primary"
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} /> : <Check />}
            >
              {loading ? 'Submitting...' : 'Confirm Request'}
            </Button>
          </DialogActions>
        </Dialog>

        <IonLoading isOpen={loading} message={'Processing...'} />
        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          duration={3000}
          color={toastType}
        />
      </IonContent>
    </IonPage>
  );
};

export default ItemRequestPage;